import React, { memo, ReactElement, useCallback } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormGroup,
  Typography,
} from '@mui/material';

import { OptionSelectionChecklistAnswerModel } from 'shared/domain/template/inspectionTemplateModel';
import { RichTextInlineView } from 'components/common/RichTextEditor/RichTextInlineView';
import { getAnswerFieldCardSx, answerFieldBodySx, answerLabelSx, optionListSx, requiredIndicatorSx } from './styles';

interface OptionSelectionInputProps {
  answerConfig: OptionSelectionChecklistAnswerModel;
  value?: string[];
  onChange: (value: string[]) => void;
  disabled?: boolean;
  hideLabel?: boolean;
}

function OptionSelectionInputComponent({
  answerConfig,
  value = [],
  onChange,
  disabled,
  hideLabel = false,
}: OptionSelectionInputProps): ReactElement {
  const isMultiselect = answerConfig.isMultiselect;

  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange([event.target.value]);
    },
    [onChange]
  );

  const handleCheckboxChange = useCallback(
    (optionId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        onChange([...value, optionId]);
      } else {
        onChange(value.filter((id) => id !== optionId));
      }
    },
    [onChange, value]
  );

  const wrapperSx = hideLabel
    ? answerFieldBodySx
    : getAnswerFieldCardSx(answerConfig.type);

  if (isMultiselect) {
    return (
      <Box sx={wrapperSx}>
        {answerConfig.label && !hideLabel ? (
          <Typography component='p' sx={answerLabelSx}>
            {answerConfig.label}
            {answerConfig.isRequired && (
              <Box component='span' sx={requiredIndicatorSx}>*</Box>
            )}
          </Typography>
        ) : null}
        <FormGroup sx={optionListSx}>
          {answerConfig.options.map((option) => (
            <FormControlLabel
              key={option._id}
              control={
                <Checkbox
                  checked={value.includes(option._id)}
                  onChange={handleCheckboxChange(option._id)}
                  disabled={disabled}
                />
              }
              label={<RichTextInlineView content={option.label} />}
            />
          ))}
        </FormGroup>
      </Box>
    );
  }

  return (
    <Box sx={wrapperSx}>
      {answerConfig.label && !hideLabel ? (
        <Typography component='p' sx={answerLabelSx}>
          {answerConfig.label}
          {answerConfig.isRequired && (
            <Box component='span' sx={requiredIndicatorSx}>*</Box>
          )}
        </Typography>
      ) : null}
      <RadioGroup
        value={value[0] ?? ''}
        onChange={handleRadioChange}
        sx={optionListSx}
      >
        {answerConfig.options.map((option) => (
          <FormControlLabel
            key={option._id}
            value={option._id}
            control={<Radio disabled={disabled} />}
            label={<RichTextInlineView content={option.label} />}
          />
        ))}
      </RadioGroup>
    </Box>
  );
}

export const OptionSelectionInput = memo(OptionSelectionInputComponent);
